import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNotifications } from '../../components/notifications';
import LoadingSpinner from '../LoadingSpinner';
import { autoplannerAPI } from '../../services/api';
import { useAutoplannerState } from '../../hooks/autoplanner/useAutoplannerState';
import { useAutoplannerTaskManager } from '../../hooks/autoplanner/useAutoplannerTaskManager';
import { useExperimentalCrews } from '../../hooks/autoplanner/useExperimentalCrews';
import Step1TrainingGroupSelection from './steps/Step1TrainingGroupSelection';
import Step2SessionSelection from './steps/Step2SessionSelection';
import Step3Validation from './steps/Step3Validation';
import Step3AvailabilitySimple from './steps/Step3AvailabilitySimple';
import Step4LockedSeatings from './steps/Step4LockedSeatings';
import Step5AlgorithmPreferences from './steps/Step5PromptSelection';
import Step6Confirmation from './steps/Step6Confirmation';
import Step7Processing from './steps/Step7Processing';
import AutoplannerResults from './steps/AutoplannerResults';
import AutoplannerHeader from './AutoplannerHeader';
import AutoplannerProgress from './AutoplannerProgress';
import AutoplannerProgressMobile from './AutoplannerProgressMobile';
import AutoplannerFooterMobile from './AutoplannerFooterMobile';

const AutoplannerWizard = ({ trainingGroups, selectedTask, onComplete, onCancel, advancedUser = false }) => {
  const { showToast } = useNotifications();
  const [validationResult, setValidationResult] = useState(null);

  const { formData, updateFormData, setFormData } = useAutoplannerState(selectedTask);
  const { currentStep, setCurrentStep, processing, setProcessing, taskId, setTaskId } =
    useAutoplannerTaskManager(selectedTask, setFormData, setValidationResult);
  const { shouldSkipLockedSeatings, loadingExperimentalCrews } = useExperimentalCrews(
    formData.trainingGroupId
  );

  // Top navigation handlers provided by each step
  const [topNav, setTopNav] = useState({
    onNext: null,
    onBack: null,
    canNext: true,
    canBack: true,
  });
  const registerNav = useCallback((handlers = {}) => {
    setTopNav((prev) => {
      const next = {
        onNext: handlers.onNext || null,
        onBack: handlers.onBack || null,
        canNext: handlers.canNext !== undefined ? handlers.canNext : true,
        canBack: handlers.canBack !== undefined ? handlers.canBack : true,
      };
      if (
        prev.onNext === next.onNext &&
        prev.onBack === next.onBack &&
        prev.canNext === next.canNext &&
        prev.canBack === next.canBack
      ) {
        return prev; // avoid re-render loop when nothing actually changed
      }
      return next;
    });
  }, []);

  // Navigation refs to avoid circular deps; step-aware logic assigned after steps are built
  const navRef = useRef({
    next: () => setCurrentStep((prev) => prev + 1),
    prev: () => setCurrentStep((prev) => prev - 1),
  });

  const goNext = useCallback(() => {
    navRef.current.next();
  }, []);

  const goBack = useCallback(() => {
    navRef.current.prev();
  }, []);

  const handleStep1Complete = useCallback(
    (trainingGroupId) => {
      updateFormData({ trainingGroupId });
      navRef.current.next();
    },
    [updateFormData]
  );

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleStep2Complete = async (sessions, startDate, endDate, sessionOrder, newTaskId) => {
    try {
      updateFormData({ selectedSessions: sessions, startDate, endDate, sessionOrder });

      // Set task ID if it was created in Step 2
      if (newTaskId && !taskId) {
        setTaskId(newTaskId);
        // Ensure the updated taskId propagates before rendering Step 3
        await Promise.resolve();
        setTimeout(() => navRef.current.next(), 0);
      } else {
        navRef.current.next();
      }
    } catch (error) {
      console.error('Error completing Step 2:', error);
      showToast('Error completing step', 'error');
    }
  };

  const handleStep3Complete = async (sessionExclusions, resolutions) => {
    try {
      // Update form data with validation results
      updateFormData({ sessionExclusions, resolutions });
      navRef.current.next();
    } catch (error) {
      console.error('Step 3 error:', error);
      showToast('Error updating task status', 'error');
    }
  };

  const handleStep3SaveDraft = async () => {
    try {
      if (!taskId) {
        console.error('No task ID available for saving draft');
        showToast('Error: No task found to save', 'error');
        return;
      }

      await autoplannerAPI.saveTaskDraft(taskId, {
        training_group_id: formData.trainingGroupId,
        start_date: formData.startDate,
        end_date: formData.endDate,
        sessions: formData.selectedSessions,
        locked_seatings: formData.lockedSeatings,
        session_exclusions: formData.sessionExclusions,
        resolutions: formData.resolutions,
        session_order: formData.sessionOrder,
      });

      if (formData.selectedSessions.length > 0) {
        const sessionIds = formData.selectedSessions.map((session) => session.id);
        await autoplannerAPI.fixTaskSessions(taskId, sessionIds);
      }

      showToast('Draft saved successfully', 'success');
      onCancel();
    } catch (error) {
      console.error('Error saving draft:', error);
      showToast('Error saving draft', 'error');
    }
  };

  const handleStep4Complete = async (lockedSeatings) => {
    try {
      updateFormData({ lockedSeatings });
      if (taskId) {
        await autoplannerAPI.saveLockedSeatings(taskId, lockedSeatings);
        // Regenerate normalized dataset so confirmation step reflects latest locked seatings
        await autoplannerAPI.createNormalizedDataset(taskId);
        await autoplannerAPI.updateAutoplannerTask(taskId, {}, 'validation_complete');
      }
      navRef.current.next();
    } catch (error) {
      console.error('Step 4 error:', error);
      showToast('Error saving locked seatings', 'error');
    }
  };

  const handleStep5Complete = async (promptType) => {
    try {
      updateFormData({ promptType });
      if (taskId) {
        await autoplannerAPI.updateAutoplannerTask(
          taskId,
          { prompt_type: promptType },
          'prompt_selected'
        );
        // Ensure normalized dataset reflects selected prompt type
        try {
          await autoplannerAPI.createNormalizedDataset(taskId);
        } catch (e) {
          console.warn('Regenerating normalized dataset after prompt selection failed', e);
        }
      }
      navRef.current.next();
    } catch (error) {
      console.error('Error updating task status:', error);
      showToast('Error updating task status', 'error');
    }
  };

  const handleStep6Complete = async () => {
    try {
      setProcessing(true);
      let currentTaskId = taskId;
      if (!currentTaskId) {
        const newTask = await autoplannerAPI.createAutoplannerTask(
          formData.trainingGroupId,
          formData.selectedSessions,
          formData.startDate,
          formData.endDate
        );
        currentTaskId = newTask.id;
        setTaskId(currentTaskId);
      }

      const taskData = {
        training_group_id: formData.trainingGroupId,
        start_date: formData.startDate,
        end_date: formData.endDate,
        prompt_type: formData.promptType,
        sessions: formData.selectedSessions,
      };

      await autoplannerAPI.updateAutoplannerTask(currentTaskId, taskData, 'dispatch');
      // Move to processing step immediately; the processing page will poll status
      navRef.current.next();
    } catch (error) {
      console.error('Error creating task:', error);
      showToast('Error creating autoplanner task', 'error');
      setProcessing(false);
    }
  };

  const selectedTaskRef = useRef(selectedTask);
  useEffect(() => {
    selectedTaskRef.current = selectedTask;
  }, [selectedTask]);

  const handleStep7Complete = useCallback(
    (showResultsInline = false) => {
      if (showResultsInline) {
        setCurrentStep(7);
        return;
      }
      const cur = selectedTaskRef.current;
      const isViewingCompletedTask = cur && cur.status === 'completed';
      onComplete(isViewingCompletedTask);
    },
    [setCurrentStep, onComplete]
  );

  const steps = useMemo(() => {
    const built = [
      {
        id: 'select-training-group',
        title: 'Select Training Group',
        component: (
          <Step1TrainingGroupSelection
            trainingGroups={trainingGroups}
            selectedGroupId={formData.trainingGroupId}
            onComplete={handleStep1Complete}
            onBack={handleCancel}
            registerNav={registerNav}
          />
        ),
      },
      {
        id: 'select-sessions',
        title: 'Select Sessions',
        component: (
          <Step2SessionSelection
            trainingGroupId={formData.trainingGroupId}
            selectedSessions={formData.selectedSessions}
            startDate={formData.startDate}
            endDate={formData.endDate}
            taskId={taskId}
            onComplete={handleStep2Complete}
            onBack={goBack}
            registerNav={registerNav}
          />
        ),
      },
      {
        id: 'validation',
        title: 'Validate Availability',
        component: (
          advancedUser ? (
            <Step3Validation
              trainingGroupId={formData.trainingGroupId}
              selectedSessions={formData.selectedSessions}
              startDate={formData.startDate}
              endDate={formData.endDate}
              taskId={taskId}
              validationResult={validationResult}
              processing={processing}
              onComplete={handleStep3Complete}
              onBack={goBack}
              onRetry={() => { }}
              onSaveDraft={handleStep3SaveDraft}
              registerNav={registerNav}
              advancedUser={advancedUser}
            />
          ) : (
            <Step3AvailabilitySimple
              trainingGroupId={formData.trainingGroupId}
              selectedSessions={formData.selectedSessions}
              startDate={formData.startDate}
              endDate={formData.endDate}
              taskId={taskId}
              onComplete={handleStep3Complete}
              onBack={goBack}
              registerNav={registerNav}
            />
          )
        ),
      },
    ];

    if (advancedUser) {
      built.push({
        id: 'locked-seatings',
        title: 'Locked Seatings',
        component: (
          <Step4LockedSeatings
            trainingGroupId={formData.trainingGroupId}
            selectedSessions={formData.selectedSessions}
            lockedSeatings={formData.lockedSeatings}
            taskId={taskId}
            onComplete={handleStep4Complete}
            onBack={goBack}
            registerNav={registerNav}
          />
        ),
      });
    }

    built.push(
      {
        id: 'preferences',
        title: 'Algorithm Preferences',
        component: (
          <Step5AlgorithmPreferences
            promptType={formData.promptType}
            onComplete={handleStep5Complete}
            onBack={goBack}
            registerNav={registerNav}
          />
        ),
      },
      {
        id: 'confirm',
        title: 'Confirm Task',
        component: (
          <Step6Confirmation
            formData={formData}
            trainingGroups={trainingGroups}
            processing={processing}
            onComplete={handleStep6Complete}
            onBack={goBack}
            taskId={taskId}
            registerNav={registerNav}
          />
        ),
      },
      {
        id: 'processing',
        title: 'Processing',
        component: (
          <Step7Processing
            taskId={taskId}
            onComplete={handleStep7Complete}
            formData={formData}
            trainingGroups={trainingGroups}
            registerNav={registerNav}
          />
        ),
      },
      {
        id: 'results',
        title: 'Results',
        component: <AutoplannerResults taskId={taskId} />,
      }
    );

    return built;
  }, [
    advancedUser,
    trainingGroups,
    formData,
    taskId,
    registerNav,
    handleCancel,
    handleStep1Complete,
    handleStep2Complete,
    handleStep3Complete,
    handleStep3SaveDraft,
    handleStep4Complete,
    handleStep5Complete,
    handleStep6Complete,
    handleStep7Complete,
    goBack,
    processing,
    validationResult,
  ]);

  // After steps are available, define step-aware next/prev via navRef to avoid circular deps
  useEffect(() => {
    navRef.current.next = () => {
      setCurrentStep((prev) => {
        let nextIndex = prev + 1;
        const nextIsLocked = steps[nextIndex] && steps[nextIndex].id === 'locked-seatings';
        if (nextIsLocked && (!advancedUser || shouldSkipLockedSeatings())) {
          if (taskId) {
            autoplannerAPI
              .updateAutoplannerTask(taskId, {}, 'validation_complete')
              .catch((error) =>
                console.error(
                  'Error updating task status after skipping Locked Seatings:',
                  error
                )
              );
          }
          nextIndex += 1;
        }
        return nextIndex;
      });
    };

    navRef.current.prev = async () => {
      let targetIndex = currentStep - 1;
      const isLocked = steps[targetIndex] && steps[targetIndex].id === 'locked-seatings';
      if (isLocked && (!advancedUser || shouldSkipLockedSeatings())) {
        targetIndex -= 1;
      }
      if (steps[targetIndex] && steps[targetIndex].id === 'locked-seatings' && taskId) {
        try {
          await autoplannerAPI.createNormalizedDataset(taskId);
        } catch (error) {
          console.warn('Error recreating normalized dataset after going back:', error);
        }
      }
      setCurrentStep(targetIndex);
    };
  }, [steps, currentStep, advancedUser, shouldSkipLockedSeatings, taskId]);

  // stepAwareNext/Prev are provided via navRef through the effect above

  // Show a global spinner only for pre-processing steps; do not hide the processing step
  const processingStepIndex = steps.findIndex((s) => s.id === 'processing');
  const currentStepId = steps[currentStep]?.id;
  if (
    processing &&
    currentStepId !== 'validation' &&
    currentStep < processingStepIndex &&
    !loadingExperimentalCrews
  ) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AutoplannerHeader selectedTask={selectedTask} onCancel={onCancel} />
      {/* Desktop stepper */}
      <div className="hidden md:block">
        <AutoplannerProgress
          steps={steps}
          currentStep={currentStep}
          onNext={() => (topNav.onNext ? topNav.onNext() : goNext())}
          onBack={() => (topNav.onBack ? topNav.onBack() : goBack())}
          canGoNext={topNav.canNext && currentStep < steps.length - 1}
          canGoBack={topNav.canBack}
        />
      </div>
      {/* Mobile compact progress */}
      <AutoplannerProgressMobile steps={steps} currentStep={currentStep} />
      <div className="mx-auto max-w-4xl px-4 pb-24 pt-6 sm:px-6 md:pb-8 lg:px-8">
        {steps[currentStep].component}
      </div>
      {/* Global mobile nav except when step provides its own sticky footer (locked seatings) or on final step */}
      {steps[currentStep]?.id !== 'locked-seatings' && currentStep < steps.length - 1 && (
        <AutoplannerFooterMobile
          onBack={() => (topNav.onBack ? topNav.onBack() : goBack())}
          onNext={() => (topNav.onNext ? topNav.onNext() : goNext())}
          canBack={topNav.canBack}
          canNext={topNav.canNext && currentStep < steps.length - 1}
          backLabel={currentStep === 0 ? 'Cancel' : 'Back'}
          nextLabel={currentStep >= steps.length - 2 ? 'Finish' : 'Next'}
        />
      )}
    </div>
  );
};

export default AutoplannerWizard;
